<template>
  <div class="min-h-screen w-full flex items-center justify-center bg-zinc-950 text-zinc-400 font-light select-none relative overflow-hidden">

    <!-- Effets de fond animés -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-pulse"></div>
      <div class="absolute bottom-1/4 right-1/4 w-96 h-96 bg-zinc-800/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
    </div>

    <!-- Grille cyberpunk -->
    <div class="absolute inset-0 bg-[linear-gradient(rgba(161,161,170,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(161,161,170,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>

    <div class="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-8">

      <!-- Code d'erreur 404 -->
      <div class="relative">
        <h1 class="text-[clamp(8rem,20vw,14rem)] font-mono font-bold text-transparent bg-clip-text bg-gradient-to-br from-zinc-200 via-zinc-500 to-zinc-800 leading-none tracking-tighter select-none">
          404
        </h1>
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="text-[clamp(8rem,20vw,14rem)] font-mono font-bold opacity-10 blur-2xl text-amber-500 leading-none tracking-tighter">
            404
          </div>
        </div>
      </div>

      <!-- Message d'erreur -->
      <div class="space-y-4 max-w-2xl mx-auto">
        <div class="flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-600">
          <span class="text-amber-500">//</span>
          <span>Erreur de Navigation</span>
          <span class="text-amber-500">//</span>
        </div>

        <h2 class="text-2xl md:text-3xl font-medium text-white tracking-tight">
          Flux de Données Introuvable
        </h2>

        <p class="text-sm md:text-base text-zinc-500 font-light leading-relaxed max-w-xl mx-auto">
          La ressource demandée n'existe pas dans la matrice système. Le chemin d'accès est invalide ou la page a été déplacée vers une autre instance du réseau.
        </p>
      </div>

      <!-- Statistiques d'erreur -->
      <div class="grid grid-cols-3 gap-4 max-w-2xl mx-auto py-8">
        <div class="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <div class="text-2xl font-mono font-semibold text-amber-500">404</div>
          <div class="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-1">Error Code</div>
        </div>
        <div class="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <div class="text-2xl font-mono font-semibold text-zinc-400">{{ currentTime }}</div>
          <div class="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-1">Timestamp</div>
        </div>
        <div class="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <div class="text-2xl font-mono font-semibold text-red-500">NULL</div>
          <div class="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-1">Resource</div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
        <button
          @click="goBack"
          class="w-full sm:w-auto px-6 py-3 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white text-sm font-mono uppercase tracking-wider border border-zinc-800 hover:border-zinc-700 transition-all duration-200 cursor-pointer focus:outline-none group"
        >
          <span class="flex items-center justify-center gap-2">
            <svg class="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour Arrière
          </span>
        </button>

        <button
          @click="goHome"
          class="w-full sm:w-auto px-6 py-3 rounded-xl bg-white hover:bg-zinc-200 text-black text-sm font-mono font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer shadow-lg shadow-white/5 hover:shadow-white/10 active:scale-[0.98] focus:outline-none"
        >
          <span class="flex items-center justify-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Console Principale
          </span>
        </button>
      </div>

      <!-- Route actuelle -->
      <div class="pt-8 max-w-2xl mx-auto">
        <div class="bg-zinc-900/20 backdrop-blur-sm border border-zinc-900 rounded-xl p-4">
          <div class="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-2">Route Demandée</div>
          <code class="text-xs font-mono text-amber-500/80 break-all">{{ currentRoute }}</code>
        </div>
      </div>

      <!-- Aide -->
      <div class="pt-4">
        <p class="text-xs text-zinc-600 font-mono">
          Besoin d'assistance ?
          <a href="mailto:support@bookmysalon.fr" class="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors ml-1">
            support@bookmysalon.fr
          </a>
        </p>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

// Heure actuelle
const currentTime = ref('')

// Route actuelle
const currentRoute = computed(() => route.fullPath)

// Mettre à jour l'heure toutes les secondes
const updateTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

let timeInterval = null

onMounted(() => {
  updateTime()
  timeInterval = setInterval(updateTime, 1000)
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})

// Navigation
const goBack = () => {
  if (window.history.length > 2) {
    router.go(-1)
  } else {
    router.push('/')
  }
}

const goHome = () => {
  router.push('/')
}
</script>

<style scoped>
/* Animation pour le pulse avec délai */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.delay-1000 {
  animation-delay: 1s;
}

/* Effet de glitch subtil sur le titre */
@keyframes glitch {
  0% {
    text-shadow: 0 0 0 transparent;
  }
  2% {
    text-shadow: 2px 0 0 rgba(245, 158, 11, 0.3);
  }
  4% {
    text-shadow: -2px 0 0 rgba(245, 158, 11, 0.3);
  }
  6% {
    text-shadow: 0 0 0 transparent;
  }
  100% {
    text-shadow: 0 0 0 transparent;
  }
}

h1 {
  animation: glitch 8s infinite;
}
</style>
